import { type JSX, useCallback, useEffect, useRef } from 'react';

/**
 * Das Feld fuer die digitale Unterschrift (TAG-52, TAG-53).
 *
 * Gezeichnet wird mit Zeigerereignissen — Finger, Stift und Maus sind damit
 * derselbe Weg. `touch-action: none` haelt den Browser davon ab, beim Zeichnen
 * die Seite zu verschieben.
 *
 * Zurueck geht ein PNG als data-URL, sobald etwas gezeichnet ist, sonst `null`.
 * Das Bild ist, was der Nachweis traegt: „das Bild der Unterschrift" (TAG-53).
 */
export function Unterschriftfeld({
  beiAenderung,
  beschriftung = 'Unterschrift',
}: {
  readonly beiAenderung: (png: string | null) => void;
  readonly beschriftung?: string;
}): JSX.Element {
  const flaeche = useRef<HTMLCanvasElement | null>(null);
  const zeichnet = useRef(false);
  const leer = useRef(true);

  useEffect(() => {
    const c = flaeche.current;
    if (c === null) return;
    /*
     * Die Aufloesung folgt der dargestellten Groesse — sonst wird die Linie
     * unscharf. Gemessen wird bei jeder Groessenaenderung neu (Auftrag
     * CRM-105): Dreht jemand das Telefon, behielt die Flaeche die alte
     * Pixelzahl, und der Strich lief verzerrt und neben dem Finger.
     */
    const einrichten = (): void => {
      const verhaeltnis = window.devicePixelRatio || 1;
      const breite = Math.round(c.clientWidth * verhaeltnis);
      const hoehe = Math.round(c.clientHeight * verhaeltnis);
      if (breite === c.width && hoehe === c.height) return;
      /* Was schon steht, wird gleichmaessig mitskaliert, nicht gestreckt. */
      const alt = !leer.current && c.width > 0 && c.height > 0 ? document.createElement('canvas') : null;
      if (alt !== null) {
        alt.width = c.width;
        alt.height = c.height;
        alt.getContext('2d')?.drawImage(c, 0, 0);
      }
      c.width = breite;
      c.height = hoehe;
      const k = c.getContext('2d');
      if (k === null) return;
      if (alt !== null) {
        const faktor = Math.min(breite / alt.width, hoehe / alt.height);
        k.drawImage(alt, 0, 0, alt.width * faktor, alt.height * faktor);
      }
      k.scale(verhaeltnis, verhaeltnis);
      k.lineWidth = 2.5;
      k.lineCap = 'round';
      k.lineJoin = 'round';
      k.strokeStyle = '#111';
    };
    einrichten();
    if (typeof ResizeObserver === 'undefined') return;
    const beobachter = new ResizeObserver(einrichten);
    beobachter.observe(c);
    return () => beobachter.disconnect();
  }, []);

  const punkt = (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const loeschen = useCallback(() => {
    const c = flaeche.current;
    const k = c?.getContext('2d');
    if (c === null || c === undefined || k === null || k === undefined) return;
    k.clearRect(0, 0, c.width, c.height);
    leer.current = true;
    beiAenderung(null);
  }, [beiAenderung]);

  return (
    <div className="unterschrift">
      <canvas
        ref={flaeche}
        className="unterschrift__flaeche"
        role="img"
        aria-label={`${beschriftung}: mit dem Finger oder Stift unterschreiben`}
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => {
          const k = e.currentTarget.getContext('2d');
          if (k === null) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          zeichnet.current = true;
          const { x, y } = punkt(e);
          k.beginPath();
          k.moveTo(x, y);
        }}
        onPointerMove={(e) => {
          if (!zeichnet.current) return;
          const k = e.currentTarget.getContext('2d');
          if (k === null) return;
          const { x, y } = punkt(e);
          k.lineTo(x, y);
          k.stroke();
          leer.current = false;
        }}
        onPointerUp={(e) => {
          if (!zeichnet.current) return;
          zeichnet.current = false;
          if (!leer.current) beiAenderung(e.currentTarget.toDataURL('image/png'));
        }}
      />
      <button type="button" className="knopf knopf--eingriff knopf--klein" onClick={loeschen}>
        Unterschrift löschen
      </button>
    </div>
  );
}
